import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import {
  distinctUntilChanged,
  filter,
  flatMap,
  map,
  scan,
  shareReplay,
  take,
  tap,
  withLatestFrom
} from 'rxjs/operators'
import { merge, Observable, Subject, ReplaySubject } from 'rxjs'
import { CategoryView } from '../../libs/category-list/category-list.interface'
import { VerticalService } from '../../singleton-services/vertical.service'
import {
  DEFAULT_OG_IMAGE_HEIGHT,
  DEFAULT_OG_IMAGE_WIDTH,
  PageMetaService,
  removeTrailingSlash
} from '../../libs/page-meta/page-meta.service'
import { PageMeta } from '../../libs/page-meta/page-meta.interface'
import { SegmentTagV2 } from '../../shared/analytics/models/segment-tag.model'
import { SegmentService } from '../../shared/analytics/services/segment.service'
import { AdConfig, AdTypes } from '../../shared/ads/advertising-service/ad-config'
import {
  AD_ADHESION_SIZE,
  AD_LEADERBOARD_LARGE_FLEX_SIZE,
  AD_LEADERBOARD_SMALL_SIZE,
  AD_RECTANGLE_LARGE_SIZE,
  AD_SKIN_SIZE
} from '../../shared/ads/ads.utility'
import { environment } from '../../../environments/environment'
import { AdComponentInputs } from '../../shared/ads/ad/ad-component.interface'
import { capitalizeFirstChar } from '../../shared/utility-functions/string.utility'
import {
  accumulateViewAll,
  filterTeamsPaginationCriteria,
  getRouteQueryParams,
  shouldShowViews
} from '../../singleton-services/category.service.utility'
import { CategoryService, VIEW_ALL_PER_PAGE_COUNT } from '../../singleton-services/category.service'
import { LOGGER_SERVICE } from '../../logger/logger.config'
import { LoggerService } from '../../logger/logger.interface'
import { SegmentEvents } from '../../shared/analytics/models/segment-events.model'
import { SearchMeta, SearchQueryParams } from '../../singleton-services/search-data.service.interface'
import { PlatformService } from '../../singleton-services/platform.service'

export const TEAMS_LIST_SEGMENT_TAG: SegmentTagV2 = {
  page_category: 'Teams',
  subpage_category: 'Teams List',
  category: 'Teams',
  autoPageCall: false
}

@Component({
  templateUrl: './teams-list.component.html',
  styleUrls: ['./teams-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TeamsListComponent implements OnInit, OnDestroy {
  // Page Meta used for infinite scroll
  private readonly pageMetaSubject = new ReplaySubject<SearchMeta | undefined>()
  // Fetching more teams for infinite scroll
  public readonly fetchTeams = new Subject<void>()
  public readonly type = 'team'
  // Page marker to trigger/reset infinite scroll
  private page = 1

  constructor(
    private readonly vs: VerticalService,
    private readonly pageMeta: PageMetaService,
    private readonly ar: ActivatedRoute,
    private readonly segment: SegmentService,
    private readonly router: Router,
    private readonly categoryService: CategoryService,
    private readonly platformService: PlatformService
  ) {}

  private distinctQueryParams$ = this.ar.queryParamMap.pipe(
    filter(() => this.platformService.isBrowser), // don't need ssr loaded data - don't call service twice
    map(queryParams => getRouteQueryParams(queryParams)),
    distinctUntilChanged((a: SearchQueryParams, b: SearchQueryParams) => {
      return a.q === b.q && a.facets === b.facets && a.limit === b.limit
    }),
    tap(_ => {
      this.page = 1 // Query params changed, reset pagination page index.
    }),
    shareReplay(1)
  )

  private queryParamsData$ = this.distinctQueryParams$.pipe(
    flatMap(qp => this.categoryService.searchTeams$(qp)),
    filter(data => data !== undefined),
    shareReplay()
  )

  private initialTeams$: Observable<readonly CategoryView[]> = this.ar.data.pipe(
    filter(data => data !== undefined),
    map((data: { teams: CategoryView[] }) => data.teams)
  )

  private pageMeta$: Observable<SearchMeta | undefined> = this.pageMetaSubject.pipe(
    filter(meta => meta !== undefined),
    shareReplay()
  )

  private sendPageCall() {
    this.segment.pageV2(TEAMS_LIST_SEGMENT_TAG, { _tag: 'list' })
  }

  private readonly paginatedTeams$ = this.fetchTeams.pipe(
    withLatestFrom(this.distinctQueryParams$, this.pageMeta$),
    filter(([_, queryParams, meta]) => filterTeamsPaginationCriteria(queryParams, meta, this.page)),
    flatMap(([_, queryParams, meta]) =>
      this.categoryService.searchTeams$(queryParams, VIEW_ALL_PER_PAGE_COUNT, ++this.page)
    ),
    tap(_ => {
      this.segmentInfiniteScrollCall(this.page)
    })
  )

  public teams$: Observable<ReadonlyArray<CategoryView> | undefined> = merge(
    this.initialTeams$,
    this.paginatedTeams$,
    this.queryParamsData$
  ).pipe(
    scan<ReadonlyArray<CategoryView> | undefined, ReadonlyArray<CategoryView> | undefined>((acc, curr) => {
      return accumulateViewAll(acc, curr)
    }, []),
    map((views: ReadonlyArray<CategoryView>) => shouldShowViews(views)),
    tap(views => this.pageMetaSubject.next(views && views[0]?.meta)),
    shareReplay(1)
  )

  public search(query: Record<string, string>) {
    this.segment.trackEvent({
      name: SegmentEvents.SEARCH_QUERY_MADE,
      properties: {
        search_term: query.q || '',
        name: `Search for Teams`
      }
    })

    this.router.navigate([], { queryParams: query, queryParamsHandling: 'merge' })
  }

  ngOnInit() {
    this.setPageMeta()
    this.sendPageCall()
    this.setAdConfig()
  }

  public ngOnDestroy() {
    this.pageMeta.clearMeta()
  }

  private segmentInfiniteScrollCall(currentPage: number): void {
    this.segment.trackEvent({
      name: SegmentEvents.INFINITE_SCROLL,
      properties: {
        page: currentPage
      }
    })
  }

  private setPageMeta(): void {
    this.vs.siteSettings$
      .pipe(
        map(settings => {
          const canonicalUrl = settings.core_site_url ? `${removeTrailingSlash(settings.core_site_url)}/teams` : ''
          const pageMeta: PageMeta = {
            // TODO: update page-meta.service to allow for schema lists,
            // @see {@link https://developers.google.com/search/docs/advanced/structured-data/sd-policies#individual-items}
            type: 'website',
            twitterCard: 'summary_large_image',
            title: `${capitalizeFirstChar(settings.meta_sport_name || 'FloSports')} | Teams - ${settings.site_name}`,
            description: `Check out your favorite teams. ${settings.meta_sport_name} live events, videos, and news.`,
            canonicalUrl,
            siteName: settings.site_name,
            image: {
              url: settings.meta_default_image ?? '',
              height: DEFAULT_OG_IMAGE_HEIGHT,
              width: DEFAULT_OG_IMAGE_WIDTH
            }
          }
          this.pageMeta.setPageMeta(pageMeta)
        }),
        take(1)
      )
      .subscribe()
  }

  // Ads
  public readonly adConfigMap = new Map<AdTypes, AdComponentInputs>()

  private setAdConfig() {
    // format the ad path,
    // see {@link https://docs.google.com/spreadsheets/d/1HzBmCrTPHRHgDJYlk-xmftJh8jkoe_rIykqH8gBtmYI/edit?usp=sharing}
    this.adConfigMap.set('skin', {
      config: {
        adConfig: AdConfig(`teams/atf`, AD_SKIN_SIZE, 'ad-skin')
      }
    })

    this.adConfigMap.set('leaderboard-large', {
      config: {
        adConfig: AdConfig(`teams/728x90_flex`, AD_LEADERBOARD_LARGE_FLEX_SIZE, 'leaderboard-large'),
        additionalOpts: { deferClick: environment.advertising.preventClickTimeout }
      }
    })

    this.adConfigMap.set('leaderboard-small', {
      config: {
        adConfig: AdConfig(`teams/728x90_flex`, AD_LEADERBOARD_SMALL_SIZE, 'leaderboard-small')
      }
    })

    this.adConfigMap.set('rectangle-small', {
      config: {
        adConfig: AdConfig(`teams/300x250_1`, AD_RECTANGLE_LARGE_SIZE, 'rectangle-small')
      }
    })

    this.adConfigMap.set('adhesion', {
      config: {
        adConfig: AdConfig(`teams/adhesion`, AD_ADHESION_SIZE, 'adhesion'),
        additionalOpts: { maxRefreshTime: 30000 }
      }
    })
  }
}
